import { useEffect, useMemo, useState, useCallback } from "react";
import sortBy from "lodash/sortBy";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";
import useAuth from "../../../../../../hooks/useAuth";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import useMutation from "../../../../../../hooks/useMutation";
import useNotification from "../../../../../../hooks/notification";
import {
  CREATE_ASSET_MUTATION,
  CREATE_PROJECT_MUTATION,
  UPDATE_ASSET_MUTATION,
} from "../../../../../../constants/graphql/mutations";
import {
  ASSETS_COLLECTION_INVENTORY_MOVE_QUERY,
  PROJECTS_NAMES_QUERY,
} from "../../../../../../constants/graphql/queries";
import { buildRackLocation } from "../utils";
import { LOCATION_SNAPSHOT_ADD_ITEM_STEPS } from "../steps";

const { CHOICE, SEARCH_RESULTS } = LOCATION_SNAPSHOT_ADD_ITEM_STEPS;

export const useLocationSnapshotAddItem = ({
  open,
  onClose,
  locationName,
  locationDetails,
  isMobileLocation,
  onAssetSelected,
}) => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const step = queryParams.get("addItemStep") || CHOICE;

  const setStep = useCallback(
    (nextStep) => {
      const params = new URLSearchParams(queryParams.toString());
      params.set("addItemStep", nextStep);
      navigate({ search: params.toString() });
    },
    [navigate, queryParams]
  );

  const [searchCode, setSearchCode] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [openCreateProject, setOpenCreateProject] = useState(false);
  const [creatingAsset, setCreatingAsset] = useState(false);
  const [selectingAssetId, setSelectingAssetId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const { user } = useAuth();
  const { notifyError, notifySuccess } = useNotification();
  const { settings } = user.company;

  const {
    data: { projects = [] } = {},
    loading: loadingProjects,
    refetch: refetchProjects,
  } = useQuery(PROJECTS_NAMES_QUERY, {
    skip: !open,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const [createAsset] = useMutation(CREATE_ASSET_MUTATION);
  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION);
  const [createProject] = useMutation(CREATE_PROJECT_MUTATION);
  const [searchAssets, { loading: searchLoading }] = useLazyQuery(
    ASSETS_COLLECTION_INVENTORY_MOVE_QUERY,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
    }
  );

  const activeProjects = useMemo(
    () =>
      sortBy(
        projects.filter((project) => project.active),
        "name"
      ),
    [projects]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSearchCode("");
    setSearchError("");
    setSearchResults([]);
    setOpenCreateProject(false);
    setSelectingAssetId(null);
    setSelectedProjectId(null);
    setQuantity(1);
  }, [open]);

  const handleClose = () => {
    setSearchCode("");
    setSearchError("");
    setSearchResults([]);
    setOpenCreateProject(false);
    setSelectingAssetId(null);
    onClose();
  };

  const handleSuccessfulAssetSelection = async asset => {
    let handledByParent = false;

    if (onAssetSelected) {
      try {
        handledByParent = Boolean(await onAssetSelected(asset));
      } catch {
        handledByParent = false;
      }
    }

    if (!handledByParent) {
      handleClose();
    }
  };

  const handleCreateAsset = async () => {
    const projectId = selectedProjectId;
    if (!projectId) {
      return;
    }

    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1) {
      notifyError({ submit: "Please enter a valid quantity (1 or more)." });
      return;
    }

    const rackLocation = buildRackLocation({
      locationDetails,
      locationName,
      isMobileLocation,
      subQty: qty,
    });

    if (!rackLocation) {
      notifyError({ submit: "Unable to determine location details for this item." });
      return;
    }

    setCreatingAsset(true);

    const response = await createAsset({
      variables: {
        projectId,
        input: {
          rackLocations: [rackLocation],
        },
      },
      onFailure: (errors) => notifyError(errors),
    });

    setCreatingAsset(false);

    const asset = response?.data?.createAsset?.asset;
    if (!asset?.id) {
      return;
    }

    notifySuccess("Item created successfully");
    await handleSuccessfulAssetSelection(asset);
  };

  const handleCreateProject = async ({ variables, onSuccess, onFailure }) => {
    const response = await createProject({
      variables: {
        clientId: variables.clientId,
        projectName: variables.projectName,
      },
      onFailure: (errors) => {
        notifyError(errors);
        onFailure?.(errors);
      },
    });

    const project = response?.data?.createProject?.project;
    if (!project?.id) {
      return;
    }

    try {
      localStorage.setItem("locationSnapshotAddItem_clientId", variables.clientId);
      if (variables.projectType) {
        localStorage.setItem("locationSnapshotAddItem_projectType", variables.projectType);
      }
    } catch {}

    notifySuccess(`${settings.filter_field2} created successfully`);
    onSuccess?.();
    setOpenCreateProject(false);
    await refetchProjects();
    setSelectedProjectId(project.id);
    setQuantity(1);
  };

  const handleSearch = async (codeOverride) => {
    const code = (typeof codeOverride === "string" ? codeOverride : searchCode).trim();
    if (!code) {
      return;
    }

    setSearchError("");
    const response = await searchAssets({
      variables: {
        search: code,
        hasImportedAssets: true,
        order: { item_id: "desc" },
        type: "Asset",
      },
    });

    const assets = response?.data?.assetsCollection?.collection || [];
    if (!assets.length) {
      setSearchError("No inventory found for that search.");
      return;
    }

    setSearchResults(assets);
    setStep(SEARCH_RESULTS);
  };

  const handleSelectSearchAsset = async (asset) => {
    if (!asset?.id) {
      return;
    }

    setSelectingAssetId(asset.id);

    const rackLocation = buildRackLocation({
      locationDetails,
      locationName,
      isMobileLocation,
    });

    if (!rackLocation) {
      notifyError({ submit: "Unable to determine location details for this item." });
      setSelectingAssetId(null);
      return;
    }

    const currentRackLocations = asset.rackLocations || [];
    const newRackLocations = [...currentRackLocations, rackLocation];

    const response = await updateAsset({
      variables: {
        id: asset.id,
        input: {
          rackLocations: newRackLocations,
        },
      },
      onFailure: (errors) => notifyError(errors),
    });

    setSelectingAssetId(null);

    if (response?.data?.updateAsset?.asset) {
      notifySuccess("Item added to location successfully");
      await handleSuccessfulAssetSelection(asset);
    }
  };

  return {
    step,
    setStep,
    searchCode,
    setSearchCode,
    searchError,
    searchResults,
    openCreateProject,
    setOpenCreateProject,
    creatingAsset,
    selectingAssetId,
    activeProjects,
    loadingProjects,
    searchLoading,
    settings,
    selectedProjectId,
    setSelectedProjectId,
    quantity,
    setQuantity,
    handleClose,
    handleCreateAsset,
    handleCreateProject,
    handleSearch,
    handleSelectSearchAsset,
  };
};
